<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';

import AgentAvailabilityAPI from 'dashboard/api/agentAvailability';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import BusinessDay from 'dashboard/routes/dashboard/settings/inbox/components/BusinessDay.vue';
import {
  timeSlotParse,
  timeSlotTransform,
  defaultTimeSlot,
  timeZoneOptions,
} from 'dashboard/routes/dashboard/settings/inbox/helpers/businessHour';

const FALLBACK_TIMEZONE = 'America/Sao_Paulo';

const PERIODS = ['full_day', 'morning', 'afternoon'];
const ABSENCE_TYPES = [
  'vacation',
  'day_off',
  'leave',
  'meeting',
  'break_time',
  'other',
];

const route = useRoute();
const router = useRouter();
const store = useStore();
const { accountId } = useAccount();
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const hasStoreOperations = computed(() =>
  isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.STORE_OPERATIONS
  )
);
const { t } = useI18n();

const dayNames = computed(() => [
  t('TEAMS_SETTINGS.WORKING_HOURS.DAYS.SUNDAY'),
  t('TEAMS_SETTINGS.WORKING_HOURS.DAYS.MONDAY'),
  t('TEAMS_SETTINGS.WORKING_HOURS.DAYS.TUESDAY'),
  t('TEAMS_SETTINGS.WORKING_HOURS.DAYS.WEDNESDAY'),
  t('TEAMS_SETTINGS.WORKING_HOURS.DAYS.THURSDAY'),
  t('TEAMS_SETTINGS.WORKING_HOURS.DAYS.FRIDAY'),
  t('TEAMS_SETTINGS.WORKING_HOURS.DAYS.SATURDAY'),
]);

const agentId = computed(() => route.params.agentId);
const agents = useMapGetter('agents/getAgents');
const agent = computed(() =>
  agents.value.find(item => `${item.id}` === `${agentId.value}`)
);

const isLoading = ref(true);
const isSaving = ref(false);
const isEnabled = ref(false);
const timeZone = ref(FALLBACK_TIMEZONE);
const timeSlots = ref([...defaultTimeSlot]);
const absences = ref([]);
const newAbsence = ref({
  start_date: '',
  end_date: '',
  period: 'full_day',
  absence_type: 'day_off',
  reason: '',
});

const timeZones = computed(() => timeZoneOptions());
const periodOptions = computed(() =>
  PERIODS.map(value => ({
    value,
    label: t(`AGENT_MGMT.AVAILABILITY.PERIODS.${value}`),
  }))
);
const absenceTypeOptions = computed(() =>
  ABSENCE_TYPES.map(value => ({
    value,
    label: t(`AGENT_MGMT.AVAILABILITY.TYPES.${value}`),
  }))
);
const hasError = computed(
  () =>
    isEnabled.value && timeSlots.value.some(slot => slot.from && !slot.valid)
);
const canAddAbsence = computed(
  () => newAbsence.value.start_date && newAbsence.value.end_date
);

const loadSchedule = async () => {
  const { data } = await AgentAvailabilityAPI.getSchedule(agentId.value);
  const parsed = timeSlotParse(data.working_hours ?? []);

  isEnabled.value = data.enabled;
  timeSlots.value = parsed.length ? parsed : [...defaultTimeSlot];
  timeZone.value = timeZones.value.some(tz => tz.value === data.timezone)
    ? data.timezone
    : FALLBACK_TIMEZONE;
};

const loadAbsences = async () => {
  const { data } = await AgentAvailabilityAPI.getAbsences(agentId.value);
  absences.value = data;
};

onMounted(async () => {
  // The route meta only drives what the menu offers; it does not stop a typed URL. Without the
  // store package these screens have no endpoints behind them, so send the person back instead
  // of rendering a page that can only fail.
  if (!hasStoreOperations.value) {
    router.replace({
      name: 'agent_list',
      params: { accountId: accountId.value },
    });
    return;
  }

  try {
    // the page can be deep-linked, so the agents store may still be empty
    if (!agents.value.length) store.dispatch('agents/get');

    await Promise.all([loadSchedule(), loadAbsences()]);
  } catch (error) {
    useAlert(t('AGENT_MGMT.AVAILABILITY.API.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
});

const onSlotUpdate = (day, slotData) => {
  timeSlots.value = timeSlots.value.map(slot =>
    slot.day === day ? slotData : slot
  );
};

const saveSchedule = async () => {
  isSaving.value = true;
  try {
    await AgentAvailabilityAPI.updateSchedule(agentId.value, {
      enabled: isEnabled.value,
      timezone: timeZone.value,
      working_hours: timeSlotTransform(timeSlots.value),
    });
    useAlert(t('AGENT_MGMT.AVAILABILITY.API.SUCCESS'));
  } catch (error) {
    useAlert(t('AGENT_MGMT.AVAILABILITY.API.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const addAbsence = async () => {
  try {
    await AgentAvailabilityAPI.createAbsence(agentId.value, newAbsence.value);
    newAbsence.value = {
      start_date: '',
      end_date: '',
      period: 'full_day',
      absence_type: 'day_off',
      reason: '',
    };
    await loadAbsences();
  } catch (error) {
    useAlert(t('AGENT_MGMT.AVAILABILITY.API.ABSENCE_ERROR'));
  }
};

const removeAbsence = async absenceId => {
  try {
    await AgentAvailabilityAPI.deleteAbsence(agentId.value, absenceId);
    await loadAbsences();
  } catch (error) {
    useAlert(t('AGENT_MGMT.AVAILABILITY.API.ABSENCE_ERROR'));
  }
};
</script>

<template>
  <SettingsLayout :is-loading="isLoading">
    <template #header>
      <BaseSettingsHeader
        :title="$t('AGENT_MGMT.AVAILABILITY.TITLE')"
        :description="
          agent
            ? $t('AGENT_MGMT.AVAILABILITY.DESCRIPTION', { name: agent.name })
            : $t('AGENT_MGMT.AVAILABILITY.TITLE')
        "
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-8">
        <div>
          <SettingsToggleSection
            v-model="isEnabled"
            :header="$t('AGENT_MGMT.AVAILABILITY.TOGGLE')"
            :description="$t('AGENT_MGMT.AVAILABILITY.TOGGLE_HELP')"
          />

          <template v-if="isEnabled">
            <SettingsFieldSection
              class="mt-6"
              :label="$t('AGENT_MGMT.AVAILABILITY.TIMEZONE')"
            >
              <ComboBox
                v-model="timeZone"
                :options="timeZones"
                class="[&>div>button]:!bg-n-alpha-black2"
              />
            </SettingsFieldSection>

            <table
              class="min-w-full mt-4 table-auto outline outline-1 -outline-offset-1 outline-n-weak rounded-xl"
            >
              <thead>
                <tr class="border-b border-n-weak">
                  <th
                    class="py-3 ltr:pl-4 ltr:pr-3 rtl:pl-3 rtl:pr-4 text-start text-heading-3 text-n-slate-12"
                  >
                    {{ $t('INBOX_MGMT.BUSINESS_HOURS.DAY.DAY') }}
                  </th>
                  <th
                    class="py-3 ltr:pr-3 rtl:pl-3 text-start text-heading-3 text-n-slate-12"
                  >
                    {{ $t('INBOX_MGMT.BUSINESS_HOURS.DAY.AVAILABILITY') }}
                  </th>
                  <th
                    class="py-3 ltr:pr-3 rtl:pl-3 text-start text-heading-3 text-n-slate-12"
                  >
                    {{ $t('INBOX_MGMT.BUSINESS_HOURS.DAY.HOURS') }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak">
                <BusinessDay
                  v-for="timeSlot in timeSlots"
                  :key="timeSlot.day"
                  :day-name="dayNames[timeSlot.day]"
                  :time-slot="timeSlot"
                  @update="data => onSlotUpdate(timeSlot.day, data)"
                />
              </tbody>
            </table>
          </template>

          <div class="flex justify-end py-4">
            <NextButton
              :label="$t('AGENT_MGMT.AVAILABILITY.SAVE')"
              :is-loading="isSaving"
              :disabled="hasError"
              @click="saveSchedule"
            />
          </div>
        </div>

        <div class="flex flex-col gap-4">
          <div>
            <h3 class="text-heading-2 text-n-slate-12">
              {{ $t('AGENT_MGMT.AVAILABILITY.ABSENCES.TITLE') }}
            </h3>
            <p class="text-body-main text-n-slate-11">
              {{ $t('AGENT_MGMT.AVAILABILITY.ABSENCES.HELP') }}
            </p>
          </div>

          <div class="flex flex-wrap items-end gap-3">
            <Input
              v-model="newAbsence.start_date"
              type="date"
              :label="$t('AGENT_MGMT.AVAILABILITY.ABSENCES.FROM')"
            />
            <Input
              v-model="newAbsence.end_date"
              type="date"
              :label="$t('AGENT_MGMT.AVAILABILITY.ABSENCES.TO')"
            />
            <div class="w-40">
              <label class="mb-1 block text-body-main text-n-slate-12">
                {{ $t('AGENT_MGMT.AVAILABILITY.ABSENCES.PERIOD') }}
              </label>
              <ComboBox v-model="newAbsence.period" :options="periodOptions" />
            </div>
            <div class="w-40">
              <label class="mb-1 block text-body-main text-n-slate-12">
                {{ $t('AGENT_MGMT.AVAILABILITY.ABSENCES.TYPE') }}
              </label>
              <ComboBox
                v-model="newAbsence.absence_type"
                :options="absenceTypeOptions"
              />
            </div>
            <Input
              v-model="newAbsence.reason"
              :label="$t('AGENT_MGMT.AVAILABILITY.ABSENCES.REASON')"
            />
            <NextButton
              :label="$t('AGENT_MGMT.AVAILABILITY.ABSENCES.ADD')"
              :disabled="!canAddAbsence"
              @click="addAbsence"
            />
          </div>

          <p
            v-if="!absences.length"
            class="text-body-main text-n-slate-11 py-2"
          >
            {{ $t('AGENT_MGMT.AVAILABILITY.ABSENCES.EMPTY') }}
          </p>
          <table
            v-else
            class="min-w-full table-auto outline outline-1 -outline-offset-1 outline-n-weak rounded-xl"
          >
            <tbody class="divide-y divide-n-weak">
              <tr v-for="absence in absences" :key="absence.id">
                <td
                  class="py-3 ltr:pl-4 rtl:pr-4 text-body-main text-n-slate-12"
                >
                  {{ absence.start_date }} — {{ absence.end_date }}
                </td>
                <td class="py-3 text-body-main text-n-slate-11">
                  {{ $t(`AGENT_MGMT.AVAILABILITY.PERIODS.${absence.period}`) }}
                </td>
                <td class="py-3 text-body-main text-n-slate-11">
                  {{
                    $t(`AGENT_MGMT.AVAILABILITY.TYPES.${absence.absence_type}`)
                  }}
                </td>
                <td class="py-3 text-body-main text-n-slate-11">
                  {{ absence.reason }}
                </td>
                <td class="py-3 ltr:pr-4 rtl:pl-4 text-end">
                  <NextButton
                    icon="i-woot-bin"
                    slate
                    sm
                    class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                    @click="removeAbsence(absence.id)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
